import { MouseEvent } from "react";
import { v4 as uuid } from "uuid";

import { Status } from "../../types";
import { ToastCustomEventDetail, ToastUpdateProps } from "./internal/types";
import { ToastProps } from "./internal/Toast";

export type toastProps = Pick<
  ToastProps,
  | "actions"
  | "aiMark"
  | "containerClassName"
  | "duration"
  | "message"
  | "onClose"
  | "onDismiss"
  | "progress"
  | "title"
  | "toastClassName"
>;

/**
 * Use to run toast methods for adding, updating, and dismissing toasts.
 *
 * @method info
 * @method success
 * @method warning
 * @method danger
 * @method promise
 * @method update
 * @method dismiss
 */
export const toast = () => {
  const toastMethods = [
    "info",
    "success",
    "warning",
    "danger",
    "promise",
    "update",
    "dismiss",
  ];
  console.error(
    "Please use one of the following toast methods:\n",
    ...toastMethods.map((m) => `\ttoast.${m}()\n`),
  );
};
type ToastCustomEvent = CustomEvent<ToastCustomEventDetail>;

const TOAST_EVENT_NAME = "hammer-toast";

const TOAST_TYPES: readonly Status[] = ["info", "success", "warning", "danger"];

const methods: Record<string, (props: toastProps) => string> = {};

TOAST_TYPES.forEach((type) => {
  methods[type] = (props: toastProps) => {
    const uniqueId = uuid();

    const ToastAddEvent: ToastCustomEvent =
      new CustomEvent<ToastCustomEventDetail>(TOAST_EVENT_NAME, {
        composed: true,
        detail: {
          id: uniqueId,
          eventType: "add",
          status: type,
          createdAt: Date.now(),
          ...props,
        },
      });
    dispatchEvent(ToastAddEvent);
    return uniqueId;
  };
});

const update = (id: string, props: Omit<ToastUpdateProps, "id">) => {
  const ToastEvent: ToastCustomEvent = new CustomEvent<ToastCustomEventDetail>(
    TOAST_EVENT_NAME,
    {
      composed: true,
      detail: {
        eventType: "update",
        id,
        ...props,
      },
    },
  );
  dispatchEvent(ToastEvent);
};

const dismiss = (id?: string) => {
  const ToastEvent: ToastCustomEvent = new CustomEvent<ToastCustomEventDetail>(
    TOAST_EVENT_NAME,
    {
      composed: true,
      detail: {
        eventType: "dismiss",
        id,
      },
    },
  );
  dispatchEvent(ToastEvent);
};

const promise = async (
  promise: Promise<string>,
  toastConfig: {
    loading: toastProps;
    success: (data: string) => Omit<toastProps, "id">;
    error: (error: string) => Omit<toastProps, "id">;
  },
): Promise<string> => {
  const { onClose, ...loadingToastConfig } = toastConfig.loading;
  let closedWhileLoading = false;

  const onCloseWhileLoading: toastProps["onClose"] = (
    e: MouseEvent<HTMLButtonElement>,
  ) => {
    toastConfig.loading.onClose?.(e);
    closedWhileLoading = true;
  };

  const id = toast.info({
    ...loadingToastConfig,
    onClose: onCloseWhileLoading,
    duration: false,
  });
  await Promise.resolve(promise)
    .then((res) => {
      if (closedWhileLoading) {
        toast.success({
          ...toastConfig.success(res),
        });
      } else {
        toast.update(id, {
          ...toastConfig.success(res),
          status: "success",
        });
      }
    })
    .catch((err) => {
      if (closedWhileLoading) {
        toast.danger({
          ...toastConfig.error(err),
        });
      } else {
        toast.update(id, {
          ...toastConfig.error(err),
          status: "danger",
        });
      }
    });
  return id;
};

/**
 * Use to add info toast to the stack.
 *
 * @example
 * import { toast } from "@servicetitan/anvil2";
 * const id = toast.info({
 *   message: "This is an info toast",
 *   title: "Info",
 * });
 */
toast.info = methods.info;
/**
 * Use to add success toast to the stack.
 *
 * @example
 * import { toast } from "@servicetitan/anvil2";
 * const id = toast.success({
 *   message: "This is an info toast",
 *   title: "Info",
 * });
 */
toast.success = methods.success;
/**
 * Use to add warning toast to the stack.
 *
 * @example
 * import { toast } from "@servicetitan/anvil2";
 * const id = toast.warning({
 *   message: "This is an info toast",
 *   title: "Info",
 * });
 */
toast.warning = methods.warning;
/**
 * Use to add danger toast to the stack.
 *
 * @example
 * import { toast } from "@servicetitan/anvil2";
 * const id = toast.danger({
 *   message: "This is an info toast",
 *   title: "Info",
 * });
 */
toast.danger = methods.danger;
/**
 * Use to update a specific toast in the stack.
 *
 * @example
 * import { toast } from "@servicetitan/anvil2";
 * const id = toast.info({
 *   message: "This is an info toast",
 *   title: "Info",
 * });
 * toast.update(id, {
 *   message: "This is an updated toast",
 *   title: "Updated",
 * });
 */
toast.update = update;
/**
 * Use to dismiss a specific toast in the stack.
 *
 * @example
 * import { toast } from "@servicetitan/anvil2";
 * const id = toast.info({
 *   message: "This is an info toast",
 *   title: "Info",
 * });
 * toast.dismiss(id);
 */
toast.dismiss = dismiss;
/**
 * Use to create a toast that updates itself based on a Promise state.
 *
 * @example
 * ```ts
 * import { toast } from "@servicetitan/anvil2";
 * toast.promise(
 *   new Promise((resolve, reject) => {
 *     setTimeout(() => {
 *       resolve("This is a success toast");
 *     }, 2000);
 *   }),
 *   {
 *     loading: {
 *       message: "This is a loading toast",
 *       title: "Loading",
 *     },
 *     success: (data) => ({
 *       message: data,
 *       title: "Success",
 *     }),
 *     error: (error) => ({
 *       message: error,
 *       title: "Error",
 *     }),
 *   },
 * );
 * ```
 */
toast.promise = promise;
